import { useFormikContext } from "formik";
import { FormValues } from "../../Form";
import { TypePicker } from "../../../components/form/TypePicker";
import { Text } from "../../../components/typography/Text";
import SliderInput from "../../../components/form/SliderInput";
import { SimulatorLayout, TotalSteps } from "./private/SimulatorLayout";
import { useNavigate } from "react-static-url-router";
import Heading from "../../../components/typography/Heading";
import Box from "../../../components/layout/Box";
import InputGroup from "../../../components/form/InputGroup";

import TooltipCustom from "../../../components/molecules/TooltipCustom";
import { useTrackFormSubmissionMutation } from "../../../queries/integrations/back_end";

const SubventionStepContent = () => {
  const { mutate: trackFormSubmission } = useTrackFormSubmissionMutation();

  const navigate = useNavigate();
  const { values, setFieldValue } = useFormikContext<FormValues>();



  // total steps
  const total = TotalSteps;
  const increment = (1/total) * 400;
  let counterProgress = (8/total) * 400;


  const shouldDisplayisTeacher = values.subvention.isOfficial === "YES";
  const shouldDisplayHasCompanySetUpSubvention =
    values.subvention.isOfficial === "NO";
  const shouldDisplayCompanySubventionFormat =
    shouldDisplayHasCompanySetUpSubvention &&
    values.subvention.hasCompanySetUpSubvention === "YES";
  const shouldSubventionAmountInput =
    shouldDisplayCompanySubventionFormat &&
    values.subvention.companySubventionFormat !== "unknown";

  if (values.subvention.isOfficial === "YES") {
    counterProgress += increment*2;
  }
  if (values.subvention.isTeacher !== "unknown") {
    counterProgress = 400;
  }

  if (values.subvention.isOfficial === "NO") {
    counterProgress += increment;
  }

  if (values.subvention.hasCompanySetUpSubvention === "YES") {
    counterProgress += increment;
  }

  if (values.subvention.companySubventionFormat !== "unknown") {
    counterProgress += increment;
  }

  if (values.subvention.amount > 0) {
    counterProgress += increment;
  }

  if (values.subvention.maxAmount > 0) {
    counterProgress += increment;
  }
  
  if (values.subvention.hasCompanySetUpSubvention === "NO") {
    counterProgress += 400;
  }

  const shouldDisableLink = () => {
    const isNotOfficial = values.subvention.isOfficial === "NO";
    const hasCompanySetUpSubvention =
      values.subvention.hasCompanySetUpSubvention === "YES";
    const companySubventionFormat =
      values.subvention.companySubventionFormat !== "unknown";
    const amount = values.subvention.amount > 0;
    const maxAmount = values.subvention.maxAmount > 0;
    const isTeacher = values.subvention.isTeacher !== "unknown";

    const shouldEnableLink: boolean =
      (isNotOfficial &&
        hasCompanySetUpSubvention &&
        companySubventionFormat &&
        amount &&
        maxAmount) ||
      (!isNotOfficial && isTeacher) ||
      (isNotOfficial && !hasCompanySetUpSubvention);

    return !shouldEnableLink;
  };

  const redirectToResultPage = () => {
    // 
    const stringifiedValues = JSON.stringify(values);
    trackFormSubmission(stringifiedValues, {
      onSuccess: ({data: {request_id}}) => {
        const params = new URLSearchParams({ simulator_result: stringifiedValues });
        navigate(`/loading?request_id=${request_id}&${params}`);
      },
    });
  };

  const tooltipFmdContent = `
  Votre employeur peut prendre en charge les frais liés à vos déplacements domicile-travail 
  réalisés à vélo. Le montant est forfaitaire et dépend de chaque employeur. 
  Les agents de la fonction publique peuvent bénéficier jusqu'à <b style="color: var(--color-primary)">200€/an</b>
  `

  return (
    <SimulatorLayout
      dynNumber={counterProgress}
      isFirstStep={false}
      onPreviousClick={() => setFieldValue("vehicle.submitted", false)}
      isLastStep={true}
      isCtaDisabled={shouldDisableLink()}
      onCtaClick={redirectToResultPage}
    >
      
      <Box padding={{ inline_end: "xl", inline_start: "xl" }}>
        <Box col>
          <Box gap="m" flex alignItems="center">
            <Heading marginY="m0" size="h5" uppercase="true">FORFAIT MOBILITÉ DURABLE</Heading>
            

            <TooltipCustom
              contentHtml={tooltipFmdContent}
            />
          </Box>
          <InputGroup gap="no">

          <TypePicker.Root fieldName="subvention[isOfficial]">
          <TypePicker.Label as={Text}>
            Êtes-vous un agent de la fonction publique ?
          </TypePicker.Label>
          <TypePicker.OptionsGroup direction="row">
            <TypePicker.Option value="YES">Oui</TypePicker.Option>
            <TypePicker.Option value="NO">Non</TypePicker.Option>
          </TypePicker.OptionsGroup>
          </TypePicker.Root>
          {shouldDisplayisTeacher && (
            <TypePicker.Root fieldName="subvention[isTeacher]">
              <TypePicker.Label as={Text}>
                Êtes-vous un(e) enseignant(e) du second cycle ?
              </TypePicker.Label>
              <TypePicker.OptionsGroup direction="row">
                <TypePicker.Option value="YES">Oui</TypePicker.Option>
                <TypePicker.Option value="NO">Non</TypePicker.Option>
              </TypePicker.OptionsGroup>
            </TypePicker.Root>
          )}
          {shouldDisplayHasCompanySetUpSubvention && (
            <TypePicker.Root fieldName="subvention[hasCompanySetUpSubvention]">
              <TypePicker.Label as={Text}>
                Votre entreprise a-t-elle mis en place le Forfait Mobilité Durable ?
              </TypePicker.Label>
              <TypePicker.OptionsGroup direction="row">
                <TypePicker.Option value="YES">Oui</TypePicker.Option>
                <TypePicker.Option value="NO">Non</TypePicker.Option>
              </TypePicker.OptionsGroup>
            </TypePicker.Root>
          )}
          {shouldDisplayCompanySubventionFormat && (
            <TypePicker.Root fieldName="subvention[companySubventionFormat]">
              <TypePicker.Label as={Text}>
                Quel est le format de l’indemnité mise en place au sein de votre
                entreprise ?
              </TypePicker.Label>
              <TypePicker.OptionsGroup direction="row">
                <TypePicker.Option value="Daily">Journalière</TypePicker.Option>
                <TypePicker.Option value="Mileage">Kilométrique</TypePicker.Option>
              </TypePicker.OptionsGroup>
            </TypePicker.Root>
          )}
          {shouldSubventionAmountInput && (
            <>
              <SliderInput.Root fieldName="subvention[amount]">
                <SliderInput.Label>
                  Quel est le montant de l’indemnité{" "}
                  {values.subvention.companySubventionFormat === "Daily"
                    ? "journalière"
                    : "kilométrique"}{" "}
                  ?
                </SliderInput.Label>
                <SliderInput.Input
                  max={1}
                  step={0.01}
                  value={values.subvention.amount}
                  unit={
                    values.subvention.companySubventionFormat === "Daily"
                      ? "€ / jour"
                      : "€ / km"
                  }
                />
              </SliderInput.Root>
              <SliderInput.Root fieldName="subvention[maxAmount]">
                <SliderInput.Label>
                  Quel est le montant maximal du Forfait Mobilité Durable par an ?
                </SliderInput.Label>
                <SliderInput.Input
                  max={800}
                  step={10}
                  value={values.subvention.maxAmount}
                  unit="€"
                />
              </SliderInput.Root>
            </>
          )}
        </InputGroup>
        </Box>
      </Box>
    </SimulatorLayout>
  );
};

export { SubventionStepContent };
